import { DestroyRef, inject, Injectable, Signal, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { GradeService } from '@app/core/services/grade.service';
import { GroupService } from '@app/core/services/group.service';
import { LevelService } from '@app/core/services/level.service';
import { PortalService } from '@app/core/services/portal.service';
import { SubjectService } from '@app/core/services/subject.service';
import { TeacherService } from '@app/core/services/teacher.service';
import { notNull, safeEmpty } from '@app/shared/utils';
import { AppSelectors } from '@app/store';
import { AppState } from '@app/store/app.state';
import { Store } from '@ngrx/store';
import { isEqual } from 'lodash';
import { Observable } from 'rxjs';
import { distinctUntilChanged, shareReplay, switchMap, tap, map, filter } from 'rxjs/operators';

type Service = (store: AppState) => Observable<any[]>;
export interface LoadData<T> {
  load: Signal<boolean>;
  data: Signal<any[]>;
}

@Injectable()
export class DataService {
  public readonly level: LoadData<any[]>;
  public readonly grade: LoadData<any[]>;
  public readonly group: LoadData<any[]>;
  public readonly subject: LoadData<any[]>;
  public readonly teacher: LoadData<any[]>;
  public readonly userGroups: LoadData<any[]>;
  private readonly _activeUser$: Observable<any>;
  private readonly _store = inject(Store);
  private readonly _destroy = inject(DestroyRef);
  private readonly _levelService = inject(LevelService);
  private readonly _gradeService = inject(GradeService);
  private readonly _groupService = inject(GroupService);
  private readonly _portalService = inject(PortalService);
  private readonly _teacherService = inject(TeacherService);
  private readonly _subjectService = inject(SubjectService);
  constructor() {
    this._activeUser$ = this.loadActiveUser();
    this.level = this.build(() => this._levelService.list().pipe(safeEmpty()));
    this.grade = this.build(() => this._gradeService.list().pipe(safeEmpty()));
    this.group = this.build(() => this._groupService.list().pipe(safeEmpty()));
    this.subject = this.build(() =>
      this._subjectService.list().pipe(
        safeEmpty(),
        map((subjects) => subjects.map((s) => ({ ...s, ids: [s.id, ...(s.others || [])] }))),
      ),
    );
    this.teacher = this.build(() => this._teacherService.list().pipe(safeEmpty()));
    this.userGroups = this.build(({ entidade_id }) => this._portalService.getUserGroups(entidade_id));
  }

  private loadActiveUser(): Observable<any> {
    return this._store
      .select(AppSelectors.ActiveUser)
      .pipe(takeUntilDestroyed(this._destroy), notNull(), distinctUntilChanged(isEqual));
  }

  private build<T>(service: Service): LoadData<T> {
    const load = signal(true);
    return {
      load: load.asReadonly(),
      data: toSignal(
        this._activeUser$.pipe(
          takeUntilDestroyed(this._destroy),
          filter((activeUser) => !!activeUser?.entidade_id),
          tap(() => load.set(true)),
          switchMap((activeUser) => service(activeUser)),
          tap(() => load.set(false)),
          shareReplay(1),
        ),
      ),
    };
  }
}
